import { useCallback, useEffect, useMemo, useRef, useState, useTransition } from "react";

import {
  ARCHIVE_SEARCH_FALLBACK_REASONS,
  ARCHIVE_SEARCH_MODES,
  ARCHIVE_SEARCH_SORTS,
} from "../services/search/archiveSearchProtocol.js";
import {
  getShowsLikeAnchorShow,
  hydrateSimilarShowResults,
  hydrateSemanticResults,
  loadArchiveSearchIndex,
  parseArchiveSearchUrl,
  runLexicalSearch,
  runSimilarShowsSearch,
  runSemanticSearch,
  serializeArchiveSearchUrl,
  shouldSubmitSemanticSearch,
  shouldTriggerSemanticSearch,
} from "../services/search/discoverySearch.js";
import { buildArchiveSearchDisplayResults } from "./archiveSearchRanking.js";

const getInitialUrlState = () => {
  return parseArchiveSearchUrl("");
};

const INITIAL_OUTCOME = {
  status: "idle",
  result: null,
  fallbackReason: "",
  unknownSemanticSlugs: [],
};

function isAbortError(error) {
  return error?.name === "AbortError";
}

function replaceArchiveSearchUrl(nextState) {
  if (typeof window === "undefined" || !window.history?.replaceState) {
    return;
  }
  const nextSearch = serializeArchiveSearchUrl(nextState);
  const nextUrl = `${window.location.pathname}${nextSearch}${window.location.hash || ""}`;
  window.history.replaceState(window.history.state, "", nextUrl);
}

export function useArchiveDiscoverySearch({
  initialState = null,
  fetchImpl,
  updateUrl = true,
} = {}) {
  const [urlState, setUrlState] = useState(() => initialState || getInitialUrlState());
  const [index, setIndex] = useState(null);
  const [indexStatus, setIndexStatus] = useState("idle");
  const [searchOutcome, setSearchOutcome] = useState(INITIAL_OUTCOME);
  const [isPending, startTransition] = useTransition();
  const searchAbortRef = useRef(null);
  const urlStateRef = useRef(urlState);

  useEffect(() => {
    urlStateRef.current = urlState;
  }, [urlState]);

  useEffect(() => {
    return () => {
      searchAbortRef.current?.abort();
    };
  }, []);

  const isSearchActive = useMemo(
    () => Boolean(urlState.query || urlState.selectedTags?.length),
    [urlState.query, urlState.selectedTags]
  );

  const ensureIndex = useCallback(async () => {
    if (index) return index;
    setIndexStatus("loading");
    try {
      const loaded = await loadArchiveSearchIndex({ fetchImpl });
      setIndex(loaded);
      setIndexStatus("ready");
      return loaded;
    } catch (error) {
      setIndexStatus("error");
      throw error;
    }
  }, [fetchImpl, index]);

  const setSearchState = useCallback((nextState) => {
    setUrlState((current) => {
      const resolved = {
        ...current,
        ...nextState,
        selectedTags: Array.isArray(nextState.selectedTags)
          ? nextState.selectedTags
          : current.selectedTags || [],
      };
      if (updateUrl) {
        replaceArchiveSearchUrl(resolved);
      }
      return resolved;
    });
  }, [updateUrl]);

  const commitOutcome = useCallback((nextOutcome) => {
    startTransition(() => {
      setSearchOutcome({
        status: nextOutcome.status || "ready",
        result: nextOutcome.result || null,
        fallbackReason: nextOutcome.fallbackReason || "",
        unknownSemanticSlugs: nextOutcome.unknownSemanticSlugs || [],
      });
    });
  }, []);

  const clearSearch = useCallback(() => {
    searchAbortRef.current?.abort();
    const nextState = {
      query: "",
      selectedTags: [],
      mode: ARCHIVE_SEARCH_MODES.LEXICAL,
      sort: ARCHIVE_SEARCH_SORTS.RELEVANCE,
    };
    if (updateUrl) {
      replaceArchiveSearchUrl(nextState);
    }
    startTransition(() => {
      setUrlState(nextState);
      setSearchOutcome(INITIAL_OUTCOME);
    });
  }, [updateUrl]);

  const runSearch = useCallback(async (overrideState = {}, options = {}) => {
    searchAbortRef.current?.abort();
    const abortController = typeof AbortController !== "undefined" ? new AbortController() : null;
    searchAbortRef.current = abortController;
    const currentUrlState = urlStateRef.current;
    const requestedState = {
      ...currentUrlState,
      ...overrideState,
      selectedTags: Array.isArray(overrideState.selectedTags)
        ? overrideState.selectedTags
        : currentUrlState.selectedTags || [],
    };
    if (updateUrl) {
      replaceArchiveSearchUrl(requestedState);
    }
    setUrlState(requestedState);

    const loadedIndex = await ensureIndex();
    if (abortController?.signal.aborted) {
      return null;
    }
    const lexical = runLexicalSearch(loadedIndex, {
      query: requestedState.query,
      selectedTags: requestedState.selectedTags,
      sort: requestedState.sort,
    });

    const semanticPreference = options.semantic || (requestedState.mode === ARCHIVE_SEARCH_MODES.SEMANTIC ? "force" : "auto");
    const wantsSemantic = semanticPreference === "force" && shouldSubmitSemanticSearch(requestedState.query);
    const autoSemantic = semanticPreference === "auto" && shouldTriggerSemanticSearch(requestedState.query, lexical, loadedIndex);
    const shouldRunSemantic = wantsSemantic || autoSemantic;

    if (!shouldRunSemantic) {
      commitOutcome({ result: lexical });
      return lexical;
    }

    setSearchOutcome((current) => ({ ...current, status: "semantic-loading" }));
    try {
      const showsLikeAnchor = getShowsLikeAnchorShow(loadedIndex, requestedState.query);
      const semanticPayload = showsLikeAnchor
        ? await runSimilarShowsSearch({
          slug: showsLikeAnchor.slug,
          fetchImpl,
          signal: abortController?.signal,
        })
        : await runSemanticSearch({
          query: requestedState.query,
          corpusVersion: loadedIndex.corpusVersion,
          fetchImpl,
          signal: abortController?.signal,
        });
      if (abortController?.signal.aborted) {
        return null;
      }
      if (semanticPayload.mode === ARCHIVE_SEARCH_MODES.LEXICAL_ONLY) {
        commitOutcome({
          result: lexical,
          fallbackReason: semanticPayload.reason || ARCHIVE_SEARCH_FALLBACK_REASONS.SEMANTIC_UNAVAILABLE,
        });
        return lexical;
      }

      const semantic = showsLikeAnchor
        ? hydrateSimilarShowResults(loadedIndex, semanticPayload, {
          anchorTitle: showsLikeAnchor.title,
          sort: requestedState.sort,
        })
        : hydrateSemanticResults(loadedIndex, semanticPayload, { sort: requestedState.sort });
      const displayResult = buildArchiveSearchDisplayResults({
        lexicalResult: lexical,
        semanticResult: semantic,
        query: requestedState.query,
        sort: requestedState.sort,
      });
      commitOutcome({
        result: displayResult,
        fallbackReason: displayResult.mode === ARCHIVE_SEARCH_MODES.SEMANTIC_STALE
          ? ARCHIVE_SEARCH_FALLBACK_REASONS.SEMANTIC_STALE
          : "",
        unknownSemanticSlugs: displayResult.unknownSlugs,
      });
      return displayResult;
    } catch (error) {
      if (isAbortError(error) || abortController?.signal.aborted) {
        return null;
      }
      commitOutcome({
        result: lexical,
        fallbackReason: ARCHIVE_SEARCH_FALLBACK_REASONS.SEMANTIC_UNAVAILABLE,
      });
      return lexical;
    }
  }, [commitOutcome, ensureIndex, fetchImpl, updateUrl]);

  return {
    clearSearch,
    ensureIndex,
    fallbackReason: searchOutcome.fallbackReason,
    index,
    indexStatus,
    isPending,
    isSearchActive,
    runSearch,
    searchResult: searchOutcome.result,
    searchStatus: searchOutcome.status,
    setSearchState,
    state: urlState,
    unknownSemanticSlugs: searchOutcome.unknownSemanticSlugs,
  };
}
